import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import apiClient, { getToken, setTokens, clearTokens } from "@/lib/api-client";

// --- Types ---

export interface Merchant {
  id: string;
  store_name: string | null;
  email: string;
  phone: string | null;
  store_type: string | null;
  // Backend merchant lifecycle (merchants.status). 'lead' = registered but
  // never completed checkout; the Stripe webhook flips it to 'trial'/'active'.
  status: "lead" | "trial" | "provisioning" | "active" | "past_due" | "churned" | "paused" | "cancelled";
  trial_expires_at: string | null;
  // Pricing tier (from GET /merchants/me) — drives frontend feature gating.
  subscription_tier: "essentials" | "performance" | "power" | null;
  billing_cycle: "monthly" | "annual" | null;
  [key: string]: unknown;
}

interface RegisterData {
  store_name: string;
  name: string;
  email: string;
  password: string;
  phone: string;
  store_type: string;
  signup_ref?: string;
}

interface AuthTokenResponse {
  access_token: string;
  refresh_token: string;
}

interface AuthContextValue {
  merchant: Merchant | null;
  loading: boolean;
  isAuthenticated: boolean;
  // True only once the merchant has completed Stripe checkout. Gates /app
  // access so a registered-but-not-checked-out account can't slip into the
  // product (e.g. by opening a new tab). subscription_tier is written by the
  // Stripe webhook on checkout; active/past_due are paying states.
  isOnboarded: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  // Re-fetch /merchants/me (used by the post-checkout finalizing screen to
  // poll until the webhook lands subscription_tier).
  reloadMerchant: () => Promise<void>;
}

// --- Context ---

const AuthContext = createContext<AuthContextValue | null>(null);

// --- Provider ---

export function AuthProvider({ children }: { children: ReactNode }) {
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = merchant !== null;
  // "Completed checkout" — NOT subscription_tier (it's NOT NULL DEFAULT
  // 'performance' in the DB, so it's set even for a never-checked-out
  // merchant and would let anyone past the gate). status='lead' is the only
  // reliable "registered but never paid" signal; the Stripe webhook flips it
  // to trial/active on checkout. (Matches backend require_active_account.)
  const isOnboarded = merchant !== null && merchant.status !== "lead";

  // Load merchant profile from existing token on mount. Bounded at 10s so
  // a stalled /merchants/me never leaves a merchant staring at a perpetual
  // spinner — we give up, clear tokens, and let ProtectedRoute route to
  // /login instead.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      clearTokens();
      setMerchant(null);
      setLoading(false);
    }, 10_000);

    apiClient
      .get<Merchant>("/merchants/me")
      .then(({ data }) => {
        if (settled) return;
        setMerchant(data);
      })
      .catch(() => {
        if (settled) return;
        clearTokens();
        setMerchant(null);
      })
      .finally(() => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        setLoading(false);
      });

    return () => {
      settled = true;
      window.clearTimeout(timeout);
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await apiClient.post<AuthTokenResponse>("/auth/login", {
      email,
      password,
    });
    setTokens(data.access_token, data.refresh_token);
    try {
      const { data: profile } = await apiClient.get<Merchant>("/merchants/me");
      setMerchant(profile);
    } catch (err) {
      clearTokens();
      throw err;
    }
  }, []);

  /**
   * @deprecated 2026-05-14 (Block C / C4 of the Online Presence sprint).
   * No UI callers remain — Login.tsx removed the "Sign in with Google"
   * button as part of the advisor-mode pivot away from Google OAuth.
   * The /auth/callback route remains mounted but is unreachable from
   * any current UI surface; this method is retained for potential
   * portal Phase-2 re-introduction (paid auto-publish tier when
   * Google verification is approved).
   *
   * Removing this method is a separate sweep with broader blast radius
   * (would also delete /auth/callback route + AuthCallback.tsx file +
   * the BE /auth/google-login + /auth/google-login/url endpoints).
   */
  const loginWithGoogle = useCallback(async () => {
    // L8 from the 2026-05-14 post-implementation review: surface the
    // @deprecated state at runtime in dev-tools (not just the IDE)
    // so any future re-wiring to this method gets a visible signal.
    // Production builds drop console.warn under the Vite optimizer
    // if configured; harmless even when retained.
    // eslint-disable-next-line no-console
    console.warn(
      "AuthContext.loginWithGoogle is @deprecated since 2026-05-14 " +
        "(Block C / C4 of the Online Presence sprint). No UI callers " +
        "remain. Retained for potential portal Phase-2 re-introduction.",
    );
    // Backend returns { url, code_verifier, state }. The PKCE code_verifier
    // and state MUST be persisted across the full-page redirect to Google
    // so /auth/callback can send them to POST /auth/google-login. Without
    // this persistence the Google OAuth flow silently breaks — the callback
    // has no verifier to present, and the exchange fails at Google.
    const { data } = await apiClient.get<{
      url: string;
      code_verifier: string;
      state: string;
    }>("/auth/google-login/url");
    sessionStorage.setItem("prep_google_code_verifier", data.code_verifier);
    sessionStorage.setItem("prep_google_state", data.state);
    window.location.href = data.url;
  }, []);

  const register = useCallback(async (payload: RegisterData) => {
    const { data } = await apiClient.post<AuthTokenResponse>(
      "/auth/register",
      payload
    );
    setTokens(data.access_token, data.refresh_token);
    try {
      const { data: profile } = await apiClient.get<Merchant>("/merchants/me");
      setMerchant(profile);
    } catch (err) {
      clearTokens();
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    // Send the refresh token to the server so it can be revoked. Without
    // this, a stolen refresh token continues to mint access tokens after
    // the user clicks logout.
    const refreshToken = localStorage.getItem("prep_refresh_token");
    try {
      await apiClient.post("/auth/logout", {
        refresh_token: refreshToken ?? null,
      });
    } catch {
      // best-effort
    }
    clearTokens();
    setMerchant(null);
    window.location.href = "/login";
  }, []);

  const refresh = useCallback(async () => {
    const refreshToken = localStorage.getItem("prep_refresh_token");
    if (!refreshToken) return;
    const { data } = await apiClient.post<AuthTokenResponse>("/auth/refresh", {
      refresh_token: refreshToken,
    });
    setTokens(data.access_token, data.refresh_token);
  }, []);

  const reloadMerchant = useCallback(async () => {
    const { data } = await apiClient.get<Merchant>("/merchants/me");
    setMerchant(data);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        merchant,
        loading,
        isAuthenticated,
        isOnboarded,
        login,
        loginWithGoogle,
        register,
        logout,
        refresh,
        reloadMerchant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// --- Hook ---

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
